/**
 * Generic Chart System — Catalog & Registry (v2.7.0)
 *
 * Registers the 5 production chart types as a json-render catalog
 * with Zod schemas for type-safe rendering.
 */

import { DonutCompositionChart } from "./DonutCompositionChart";
import { GroupedComparisonBarChart } from "./GroupedComparisonBarChart";
import { HorizontalMixBarChart } from "./HorizontalMixBarChart";
import { LineChart } from "./LineChart";
import React from "react";
import { VerticalBarChart } from "./VerticalBarChart";
import { defineCatalog } from "@json-render/core";
import { defineRegistry } from "@json-render/react";
import { schema } from "@json-render/react/schema";
import { z } from "zod";

// ─── Zod Schemas ───

const lineDataPointSchema = z
  .object({
    label: z.string().min(1),
    value: z.number(),
  })
  .strict();

const referenceLineSchema = z
  .object({
    label: z.string().min(1),
    value: z.number(),
  })
  .strict();

const barItemSchema = z
  .object({
    label: z.string().min(1),
    value: z.number(),
    highlight: z.boolean().optional(),
  })
  .strict();

const comparisonGroupSchema = z
  .object({
    label: z.string().min(1),
    values: z.array(z.number()),
  })
  .strict();

const categoryItemSchema = z
  .object({
    label: z.string().min(1),
    value: z.number(),
    highlight: z.boolean().optional(),
  })
  .strict();

const donutSegmentSchema = z
  .object({
    label: z.string().min(1),
    value: z.number(),
    color: z.string().optional(),
  })
  .strict();

const chartBaseSchema = z
  .object({
    type: z.string(),
    label: z.string().min(1),
  })
  .strict();

const unitField = { unit: z.string().optional() };

const lineChartDataSchema = chartBaseSchema
  .extend({
    type: z.literal("LineChart"),
    ...unitField,
    dataPoints: z.array(lineDataPointSchema).min(2).max(40),
    referenceLine: referenceLineSchema.optional(),
  })
  .strict();

const verticalBarChartDataSchema = chartBaseSchema
  .extend({
    type: z.literal("VerticalBarChart"),
    ...unitField,
    bars: z.array(barItemSchema).min(2).max(8),
  })
  .strict();

const groupedComparisonBarChartDataSchema = chartBaseSchema
  .extend({
    type: z.literal("GroupedComparisonBarChart"),
    ...unitField,
    series: z.array(z.string().min(1)).min(2).max(4),
    groups: z.array(comparisonGroupSchema).min(2).max(6),
  })
  .strict();

const horizontalMixBarChartDataSchema = chartBaseSchema
  .extend({
    type: z.literal("HorizontalMixBarChart"),
    ...unitField,
    categories: z.array(categoryItemSchema).min(2).max(8),
  })
  .strict();

const donutCompositionChartDataSchema = chartBaseSchema
  .extend({
    type: z.literal("DonutCompositionChart"),
    ...unitField,
    segments: z.array(donutSegmentSchema).min(2).max(6),
  })
  .strict();

const strictChartDataSchema = z.discriminatedUnion("type", [
  lineChartDataSchema,
  verticalBarChartDataSchema,
  groupedComparisonBarChartDataSchema,
  horizontalMixBarChartDataSchema,
  donutCompositionChartDataSchema,
]);

const ctxSchema = z.object({
  frame: z.number(),
  fps: z.number(),
  slideStart: z.number(),
  S: z.number(),
  embed: z.boolean().optional(),
  featuredTicker: z.string().optional(),
  compositionHeight: z.number().optional(),
});

const chartPropsSchema = chartBaseSchema
  .extend({
    ctx: ctxSchema.optional(),
  })
  .passthrough();

function chartDef(description: string) {
  return { props: chartPropsSchema, description };
}

// ─── Catalog ───

const edgenChartCatalog = defineCatalog(schema, {
  components: {
    LineChart: chartDef("Line chart for continuous trends over time"),
    VerticalBarChart: chartDef(
      "Vertical bar chart for discrete period comparisons",
    ),
    GroupedComparisonBarChart: chartDef(
      "Grouped bars for dual-series comparison",
    ),
    HorizontalMixBarChart: chartDef(
      "Horizontal bars for category ranking/structure",
    ),
    DonutCompositionChart: chartDef("Donut chart for composition/proportions"),
  },
  actions: {},
});

// ─── Registry ───

function wrapChart(Component: React.FC<any>) {
  // eslint-disable-next-line react/display-name
  return ({ props }: { props: any }) => {
    const { ctx, ...chartData } = props;
    return <Component chartData={chartData} ctx={ctx} />;
  };
}

export const { registry: edgenChartRegistry } = defineRegistry(
  edgenChartCatalog,
  {
    components: {
      LineChart: wrapChart(LineChart),
      VerticalBarChart: wrapChart(VerticalBarChart),
      GroupedComparisonBarChart: wrapChart(GroupedComparisonBarChart),
      HorizontalMixBarChart: wrapChart(HorizontalMixBarChart),
      DonutCompositionChart: wrapChart(DonutCompositionChart),
    },
    actions: {},
  },
);

export const validateEdgenChartData = (chartData: unknown): boolean => {
  if (!chartData || typeof chartData !== "object" || Array.isArray(chartData)) {
    return false;
  }
  const { ctx: _ctx, ...pureChartData } = chartData as Record<string, unknown>;
  return strictChartDataSchema.safeParse(pureChartData).success;
};
